// src/components/core/ActiveFilters.jsx
import React, { memo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  useTheme,
  alpha
} from '@mui/material';

/**
 * Unified component to display active filters with the option to clear them
 * 
 * @param {Object} props - Component props
 * @param {string} props.searchTerm - Current search term
 * @param {Object} props.filters - Object containing all filter values
 * @param {Function} props.onClearSearch - Function to clear search
 * @param {Function} props.onClearFilter - Function to clear a specific filter
 * @param {Function} props.onClearAllFilters - Function to clear all filters
 * @param {Array} props.filterConfig - Configuration for filter display
 * @param {boolean} props.additionalFilters - Additional filter states
 */
const ActiveFilters = ({
  searchTerm,
  filters = {},
  onClearSearch,
  onClearFilter,
  onClearAllFilters,
  filterConfig = [],
  additionalFilters = {},
  showCompletedTasks = true,
  onToggleShowCompleted
}) => {
  const theme = useTheme();
  
  // Check if any filters are active
  const hasActiveFilters = 
    !!(searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) || 
    Object.entries(filters).some(([key, value]) => value !== 'all') ||
    Object.values(additionalFilters).some(value => value === true) ||
    !showCompletedTasks; // Only count as active filter when HIDING completed tasks
  
  if (!hasActiveFilters) return null;
  
  // Get label for a filter value based on the filter configuration
  const getFilterLabel = (filterName, filterValue) => {
    const config = filterConfig.find(config => config.name === filterName);
    
    if (!config) return `${filterName}: ${filterValue}`;
    
    const option = config.options.find(opt => opt.value === filterValue);
    
    if (option) {
      return `${config.label}: ${option.label}`;
    }
    
    return `${config.label}: ${filterValue}`;
  };
  
  return (
    <Box sx={{ 
      mb: 3, 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: 1.5, 
      alignItems: 'center' 
    }}>
      <Typography variant="body2" sx={{ 
        fontSize: '0.875rem',
        fontWeight: 500,
        background: `linear-gradient(90deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent'
      }}>
        Active filters:
      </Typography>
      
      {/* Search filter */}
      {searchTerm && typeof searchTerm === 'string' && searchTerm.trim() && (
        <Chip 
          label={`Search: ${searchTerm}`}
          size="small"
          onDelete={onClearSearch}
          sx={{ 
            borderRadius: theme.shape.borderRadius * 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            '& .MuiChip-deleteIcon': {
              color: theme.palette.text.secondary,
              transition: 'all 0.2s ease',
              '&:hover': {
                color: theme.palette.secondary.main,
              },
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
            },
          }}
        />
      )}
      
      {/* Regular filters */}
      {Object.entries(filters).map(([key, value]) => {
        if (value === 'all') return null;
        
        return (
          <Chip 
            key={key}
            label={getFilterLabel(key, value)}
            size="small"
            onDelete={() => onClearFilter(key, 'all')}
            sx={{ 
              borderRadius: theme.shape.borderRadius * 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              '& .MuiChip-deleteIcon': {
                color: theme.palette.text.secondary,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: theme.palette.secondary.main,
                },
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              },
            }}
          />
        );
      })}
      
      {/* Additional boolean filters */}
      {Object.entries(additionalFilters).map(([key, value]) => {
        if (!value) return null;
        
        const config = filterConfig.find(config => config.name === key);
        const label = config ? config.label : key;
        
        return (
          <Chip 
            key={key}
            label={label}
            size="small"
            onDelete={() => onClearFilter(key, false)}
            sx={{ 
              borderRadius: theme.shape.borderRadius * 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              '& .MuiChip-deleteIcon': {
                color: theme.palette.text.secondary,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: theme.palette.secondary.main,
                },
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              },
            }}
          />
        );
      })}
      
      {/* Show Completed Tasks filter */}
      {!showCompletedTasks && (
        <Chip 
          label="Hide Completed Tasks"
          size="small"
          onDelete={() => onToggleShowCompleted && onToggleShowCompleted(true)}
          sx={{ 
            borderRadius: theme.shape.borderRadius * 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            '& .MuiChip-deleteIcon': {
              color: theme.palette.text.secondary,
              transition: 'all 0.2s ease',
              '&:hover': {
                color: theme.palette.secondary.main,
              },
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
            },
          }}
        />
      )}
      
      {/* Clear all button - only show if more than one filter is active */}
      {(() => {
        const activeFilterCount = 
          (searchTerm && typeof searchTerm === 'string' && searchTerm.trim() ? 1 : 0) +
          Object.values(filters).filter(v => v !== 'all').length +
          Object.values(additionalFilters).filter(v => v === true).length +
          (!showCompletedTasks ? 1 : 0);
        return activeFilterCount > 1;
      })() && (
        <Button
          variant="text"
          onClick={onClearAllFilters}
          size="small"
          sx={{ 
            borderRadius: theme.shape.borderRadius,
            color: theme.palette.secondary.main,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              color: theme.palette.secondary.light,
              backgroundColor: alpha(theme.palette.secondary.main, 0.05),
            },
          }}
        >
          Clear all
        </Button>
      )}
    </Box>
  );
};

export default memo(ActiveFilters);